import { memo } from 'react';
import { RenderUnsettledUI, SmallError, SmallLoader } from 'shared/components';
import { RawDocumentDetailData } from 'hq/services/documents-service.types';
import { FormProvider } from 'react-hook-form';
import { Box, styled } from '@mui/material';
import { LWModal } from 'redesign';
import { Header } from './header';
import { Buttons } from './buttons';
import { Body } from './body';
import { useRenderDocument } from '../../table/hooks/use-render-document';
import { useDocumentDetail } from '../../table/hooks/use-get-document-detail';
import { useDocumentDialogSign } from '../../table/hooks/use-document-dialog-sign';

type DocumentReviewDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
};

type DocumentData = {
  documentUrl: string;
} & RawDocumentDetailData;

type DocumentReviewDialogContentProps = DocumentReviewDialogProps & {
  documentData: DocumentData;
};

export const DocumentReviewDialog = memo(
  ({ isOpen, onClose, documentId }: DocumentReviewDialogProps) => {
    const renderDocument = useRenderDocument(documentId);
    const documentDetailQuery = useDocumentDetail(documentId);

    if (renderDocument.status !== 'success') {
      return (
        <LWModal isOpen={isOpen} onClose={onClose}>
          <RenderUnsettledUI
            renderLoading={() => <SmallLoader />}
            renderError={(error) => <SmallError error={error} />}
            data={renderDocument}
          />
        </LWModal>
      );
    }

    if (documentDetailQuery.status !== 'success') {
      return (
        <LWModal isOpen={isOpen} onClose={onClose}>
          <RenderUnsettledUI
            renderLoading={() => <SmallLoader />}
            renderError={(error) => <SmallError error={error} />}
            data={documentDetailQuery}
          />
        </LWModal>
      );
    }

    const documentData = {
      documentUrl: renderDocument.data.url,
      ...documentDetailQuery.data,
    };

    return (
      <DocumentReviewDialogContent
        documentId={documentId}
        isOpen={isOpen}
        onClose={onClose}
        documentData={documentData}
      />
    );
  }
);

export const DocumentReviewDialogContent = ({
  isOpen,
  onClose,
  documentId,
  documentData,
}: DocumentReviewDialogContentProps) => {
  const {
    firstName,
    lastName,
    infix,
    phone,
    email,
    documentUrl,
    status,
    contractStartDate,
    contractEndDate,
    type,
  } = documentData;
  const { methods, handleSubmit } = useDocumentDialogSign(
    documentId,
    onClose,
    contractStartDate,
    contractEndDate
  );

  return (
    <LWModal isOpen={isOpen} onClose={onClose}>
      <DialogLayout>
        <Header
          onClose={onClose}
          firstName={firstName}
          lastName={lastName}
          infix={infix}
          phoneNumber={phone}
          email={email}
        />
        <FormProvider {...methods}>
          <Body documentUrl={documentUrl} type={type} status={status} />
          <Buttons onCancel={onClose} onSubmit={handleSubmit} />
        </FormProvider>
      </DialogLayout>
    </LWModal>
  );
};

const DialogLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(8),
  height: '100%',
}));
